import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useForm, Controller } from 'react-hook-form';
import * as yup from 'yup';
import { Input } from '@components/Input';
import TextEditor from '@components/TextEditor';
import { Typography } from '@components/Typography';
import { useTranslation, useYupValidationResolver } from '@hooks';
import { replyTemplatesService } from '@services';
import { alertActions } from '@actions';
import { handleApiError } from '@helpers/api';
import { parseTipTapJSONToPlainText, testEditorLength } from '@helpers/text-editor';
import PostDynamicVariables from '@features/PostDynamicVariables';
import { PLACEHOLDER_CONTEXT, REPLY_TEMPLATE_PLACEHOLDER_TOKENS } from '@constants/placeholders';
import { Modal, Content } from './styles';

export const FORM_TYPE = {
    NEW: 'NEW',
    EDIT: 'EDIT',
    EDIT_COPY: 'EDIT_COPY',
    VIEW: 'VIEW',
    DELETE: 'DELETE',
};

const ReplyTemplateManageModal = ({ selectedTemplate, modal, onCloseModal, refetchCallback }) => {
    const user = useSelector(state => state.auth.user);

    const { t } = useTranslation();
    const dispatch = useDispatch();

    const ReplyTemplateSchema = yup.object().shape({
        name: yup.string().required('validation.required'),
        author: yup.string().notRequired(),
        text: yup
            .string()
            .required('validation.required')
            .test('text-editor-length', `text-editor-length`, value =>
                testEditorLength(value, 1000, REPLY_TEMPLATE_PLACEHOLDER_TOKENS),
            ),
    });

    const resolver = useYupValidationResolver(ReplyTemplateSchema);

    const {
        getValues,
        setValue,
        control,
        handleSubmit,
        formState: { isValid, isDirty, isSubmitting },
    } = useForm({
        mode: 'all',
        resolver,
        defaultValues: { name: '', author: `${user.firstName} ${user.lastName}`, text: '' },
    });

    useEffect(() => {
        if (modal.type !== FORM_TYPE.NEW) {
            setValue('name', selectedTemplate.name, { shouldValidate: true, shouldDirty: false });
            setValue('author', selectedTemplate.createdByName, { shouldValidate: true });
            setValue('text', selectedTemplate.text, { shouldValidate: true });
        }
    }, [selectedTemplate, modal.type, setValue]);

    const RESPONSE_MSG = {
        [FORM_TYPE.EDIT]: {
            success: t('alertMessages.editSuccess'),
            error: t('alertMessages.editFail'),
        },
        [FORM_TYPE.NEW]: {
            success: t('alertMessages.createSuccess'),
            error: t('alertMessages.createFail'),
        },
        [FORM_TYPE.EDIT_COPY]: {
            success: t('alertMessages.createSuccess'),
            error: t('alertMessages.createFail'),
        },
    };

    const onSubmit = async () => {
        const { name, text } = getValues();
        try {
            await replyTemplatesService.addOrUpdateReplyTemplate({
                name,
                text,
                ...(modal.type === FORM_TYPE.EDIT && { id: selectedTemplate.id }),
            });
        } catch (error) {
            handleApiError(error, RESPONSE_MSG[modal.type].error);
            return;
        }

        dispatch(alertActions.success(RESPONSE_MSG[modal.type].success));
        refetchCallback();
        onCloseModal();
    };

    const isView = modal.type === FORM_TYPE.VIEW;

    return (
        <Modal
            fullScreen
            isOpen={modal.open}
            handleClose={onCloseModal}
            isDirty={isDirty}
            title={
                modal.type === FORM_TYPE.NEW || modal.type === FORM_TYPE.EDIT_COPY
                    ? t('ReviewsReplyTemplates.addTemplate')
                    : modal.type === FORM_TYPE.VIEW
                      ? t('ReviewsReplyTemplates.viewTemplate')
                      : t('ReviewsReplyTemplates.editTemplate')
            }
            onPrimaryAction={isView ? null : handleSubmit(onSubmit)}
            primaryActionText={t('buttons.save')}
            primaryActionDisabled={isSubmitting || !isValid || !isDirty}
        >
            <Content>
                <Controller
                    control={control}
                    name="name"
                    render={({ field, fieldState: { error }, ref }) => (
                        <Input
                            ref={ref}
                            inputProps={{ ...field, onChange: e => field.onChange(e.target.value) }}
                            label={t('common.templateName')}
                            fullWidth
                            error={!!error}
                            helperText={error ? t(error.message) : null}
                            required
                            disabled={isView}
                            sx={{ gridArea: 'input1' }}
                        />
                    )}
                />
                <Typography
                    variant="caption"
                    sx={{ color: 'var(--text-secondary)', display: 'block', gridArea: 'text', paddingBottom: '16px' }}
                >
                    {t('ReviewsReplyTemplates.messageInputText')}
                </Typography>
                <Controller
                    control={control}
                    name="text"
                    render={({ field, fieldState: { error }, ref }) => (
                        <div style={{ gridArea: 'input3' }}>
                            <TextEditor
                                {...field}
                                ref={ref}
                                label={t('common.message', { count: 1 })}
                                limit={1000}
                                initialValue={selectedTemplate?.text || ''}
                                onChange={text =>
                                    setValue('text', parseTipTapJSONToPlainText(text), {
                                        shouldValidate: true,
                                        shouldDirty: true,
                                    })
                                }
                                editable={!isView}
                                error={error}
                                placeholderContext={PLACEHOLDER_CONTEXT.REPLY_TEMPLATE}
                                toolbar={<PostDynamicVariables disabled={isView} />}
                            />
                        </div>
                    )}
                />
            </Content>
        </Modal>
    );
};

export default ReplyTemplateManageModal;
